/**
 * Where a streaming reply may be cut for read-back, and where it may not.
 *
 * The agent's reply arrives as `text_delta` events and used to be spoken only
 * once the whole thing had landed — the user read four sentences, then waited,
 * then heard them. Speaking sentence by sentence as they arrive removes that
 * wait, and the entire risk of doing so lives in one question: is this period
 * the end of a sentence, or the middle of a number?
 *
 * Two separate hazards, and they need different handling:
 *
 *   1. A cut inside `$1,250.00`, `3.5`, `J-4312` or `Mr.` sends half a token to
 *      the synthesiser. "one thousand two hundred fifty dollars and" … "zero
 *      zero" is not what the operator asked for, and the API's own
 *      `preprocessForTTS` cannot repair it — it runs per request and sees only
 *      the half it was given.
 *   2. A cut at the very END of the buffer is a guess about text that has not
 *      arrived. `3.` looks like a sentence boundary until the next delta makes
 *      it `3.5`. So a boundary is only honoured with confirmed text after it;
 *      the trailing run is always held back for the flush.
 *
 * Hazard 2 is the one that cannot be fixed by a better abbreviation list, and
 * it is why this module takes the whole accumulated buffer rather than each
 * delta: the decision needs the characters on both sides of the cut.
 */

/**
 * Shortest first SENTENCE. Only a one-word acknowledgement ("Sure.", "Okay.")
 * sits under it, and it rides along with the sentence after it, which streams
 * in within a fraction of a second.
 *
 * This was 40, and with it the first chunk was the first sentence of 40+
 * characters. That reads as "speak as soon as a sentence lands" and on the
 * agent's actual voice replies it was not: they are two or three sentences and
 * the FIRST one is usually most of the reply (measured 2026-10-22 on Smith: a
 * 230-character opener in a 290-character reply). A sentence cut at that size
 * starts speech at ~85% of the stream, then waits on synthesis of 230
 * characters — the "4+ seconds late, after the whole reply is typed" report.
 */
export const MIN_FIRST_CHUNK = 12;

/**
 * Shortest first chunk cut at a CLAUSE rather than a sentence — `, ` `; ` `: `
 * or a spaced dash. Only the first chunk is ever cut this way, because only the
 * first chunk is waited on: once audio is playing, later chunks have the length
 * of the one in front of them to arrive, and a sentence boundary reads better.
 */
export const MIN_FIRST_CLAUSE = 40;

/**
 * Later chunks merge up to this before cutting at the next boundary. Once audio
 * is playing, the gap between chunks costs more than time-to-first-word, and a
 * longer chunk is one fewer round trip to fall into that gap.
 */
export const MAX_MERGED_CHUNK = 150;

/**
 * Trailing words that end in a period without ending a sentence. Matched
 * case-sensitively on the word immediately before the period.
 */
const ABBREVIATIONS = [
  'Mr', 'Mrs', 'Ms', 'Dr', 'Prof', 'Sr', 'Jr',
  'St', 'Ave', 'Rd', 'Blvd',
  'Inc', 'Ltd', 'Co', 'Corp',
  'No', 'Nos', 'Fig', 'Est',
  'approx', 'etc', 'vs', 'i.e', 'e.g', 'al',
  'Jan', 'Feb', 'Mar', 'Apr', 'Jun', 'Jul', 'Aug', 'Sept', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * True when the period at `idx` closes an abbreviation rather than a sentence.
 *
 * Reads backwards to the start of the word, so `Mr` matches in `Hi Mr. Smith`
 * and `Amr` does not — a substring test would swallow the wrong one.
 */
function endsAbbreviation(text: string, idx: number): boolean {
  let start = idx;
  while (start > 0 && /[A-Za-z.]/.test(text[start - 1])) start--;
  const word = text.slice(start, idx);
  return ABBREVIATIONS.some((a) => a.toLowerCase() === word.toLowerCase());
}

/**
 * True when the character at `idx` sits inside an identifier — a run with no
 * spaces carrying both letters and digits, or a hyphen between them.
 *
 * `J-4312` and `INV-00007` are spoken as units. This is deliberately wider than
 * a list of prefixes: the vocabulary of ticket, job and invoice numbering is
 * per-tenant and a list would be wrong for the next tenant.
 */
function insideIdentifier(text: string, idx: number): boolean {
  let start = idx;
  while (start > 0 && !/\s/.test(text[start - 1])) start--;
  let end = idx;
  while (end < text.length && !/\s/.test(text[end])) end++;
  const run = text.slice(start, end);
  return /[A-Za-z]/.test(run) && /[0-9]/.test(run);
}

/**
 * True when `idx` (the index of a `.`/`!`/`?`) is a real sentence end.
 *
 * Requires a following character, so the caller can never treat the end of an
 * unfinished buffer as a boundary — hazard 2 above.
 */
export function isSentenceEnd(text: string, idx: number): boolean {
  const ch = text[idx];
  if (ch !== '.' && ch !== '!' && ch !== '?') return false;

  const next = text[idx + 1];
  if (next === undefined) return false;  // nothing confirmed after it yet

  /*
   * A sentence end is followed by whitespace or a closer. This one test is
   * what protects every decimal and money amount: the `.` in `3.5` and in
   * `$1,250.00` is followed by a DIGIT, so it never reaches the checks below.
   *
   * There was a separate digit-on-both-sides guard here. Deleting it reddened
   * nothing, because this line had already rejected every input it could fire
   * on — two guards for one rule, and no test could tell them apart. The
   * `a period followed by a digit` cases below pin the surviving one.
   */
  if (!/[\s"')\]]/.test(next)) return false;

  if (insideIdentifier(text, idx)) return false;
  if (ch === '.' && endsAbbreviation(text, idx)) return false;

  return true;
}

/**
 * True when `idx` closes a clause the first chunk may be cut after.
 *
 * Same rule as a sentence end — confirmed text after it, never the end of the
 * buffer — plus one of its own: a comma followed by a digit is a date or a
 * list of numbers (`Oct 24, 2026`), not a pause, so it waits for a later cut.
 */
export function isClauseEnd(text: string, idx: number): boolean {
  const ch = text[idx];
  const isDash = ch === '\u2014' || ch === '\u2013';
  if (ch !== ',' && ch !== ';' && ch !== ':' && !isDash) return false;
  if (!/\s/.test(text[idx + 1] ?? '')) return false;
  // A dash is a clause break only when spaced on both sides.
  if (isDash && !/\s/.test(text[idx - 1] ?? '')) return false;
  const after = text[idx + 2];
  if (after === undefined) return false;  // nothing confirmed after the space yet
  if (ch === ',' && /[0-9]/.test(after)) return false;
  return true;
}

export interface CutResult {
  /** Text to speak now, or null when nothing may be cut yet. */
  chunk: string | null;
  /** Index in `text` at which the next chunk starts. Unchanged when chunk is null. */
  consumed: number;
}

/**
 * Find the next speakable chunk in `text` starting at `from`.
 *
 * `isFirst` selects the sizing rule: the first chunk cuts at the first legal
 * boundary past MIN_FIRST_CHUNK so speech starts early, later chunks keep
 * merging until MAX_MERGED_CHUNK so the queue stays ahead of playback.
 *
 * Never returns the tail of `text` — see hazard 2. Use `flushChunk` for that.
 */
export function nextChunk(text: string, from: number, isFirst: boolean): CutResult {
  const minLen = isFirst ? MIN_FIRST_CHUNK : MAX_MERGED_CHUNK;
  let lastBoundary = -1;
  // Parentheses are never split: `J-0077 (Excaliber 8, production)` is one unit.
  let depth = 0;

  for (let i = from; i < text.length; i++) {
    const ch = text[i];
    if (ch === '(' || ch === '[') depth++;
    else if ((ch === ')' || ch === ']') && depth > 0) depth--;

    if (isFirst && depth === 0 && isClauseEnd(text, i)) {
      const candidate = text.slice(from, i + 1).trim();
      if (candidate.length >= MIN_FIRST_CLAUSE) {
        return { chunk: candidate, consumed: i + 1 };
      }
    }

    if (!isSentenceEnd(text, i)) continue;

    // Take the whole closing run: `heavy." ` cuts after the quote, not the period.
    let end = i + 1;
    while (end < text.length && /["')\]]/.test(text[end])) end++;

    const candidate = text.slice(from, end).trim();
    if (candidate.length >= minLen) {
      return { chunk: candidate, consumed: end };
    }
    // Too short — remember it and keep merging forward.
    lastBoundary = end;
  }

  /*
   * A run of short sentences followed by a long one would otherwise wait in
   * silence for the long one to terminate — the exact delay this module exists
   * to remove. So once the unterminated TAIL has itself grown past the target,
   * cut at the last boundary and let the short sentences go.
   *
   * Measured from `lastBoundary`, not from `from`: measuring from `from` cuts
   * as soon as the whole buffer passes the target, which hands back a stub
   * ("Yes. No. Maybe.") while a perfectly good boundary was seconds away. The
   * chunk this branch emits may be under the minimum, and that is the trade —
   * it is taken only when the alternative is a long silence.
   */
  /*
   * The first chunk's minimum is a dozen characters, so measured against it this
   * would let "Sure." go the moment twelve more characters arrived. A long
   * second sentence is the case this branch exists for, and the clause cut
   * already uses MIN_FIRST_CLAUSE as "long", so it is the same yardstick here.
   */
  const tailMin = isFirst ? Math.max(minLen, MIN_FIRST_CLAUSE) : minLen;
  if (lastBoundary > 0 && text.length - lastBoundary >= tailMin) {
    return { chunk: text.slice(from, lastBoundary).trim(), consumed: lastBoundary };
  }

  return { chunk: null, consumed: from };
}

/**
 * Everything left after `from`, for the end of the stream.
 *
 * The held-back tail is only safe to speak once no more deltas are coming, so
 * this is the one place the end of the buffer may be cut.
 */
export function flushChunk(text: string, from: number): CutResult {
  const rest = text.slice(from).trim();
  if (!rest) return { chunk: null, consumed: from };
  return { chunk: rest, consumed: text.length };
}
